import React from 'react';
import { useTranslation } from 'react-i18next';
import { View, Pressable, ScrollView } from 'react-native';
import { Card } from '@/components/ui/card';
import { Text } from '@/components/ui/text';
import { Icon } from '@/components/ui/icon';
import { Icon as GlobalIcon } from '~/global/components/icon/Icon';
import {
  Trash2,
  MoreVertical,
  ChevronUp,
  ChevronDown,
} from 'lucide-react-native';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { logger } from '@/lib/logger';
import { useMutation } from '@tanstack/react-query';
import { useDeleteProtection } from '~/global/hooks/useDeleteProtection';
import { useLivePowerSyncQuery } from '~/powersync/hooks/useLivePowerSyncQuery';
import { usePowerSync } from '~/powersync/contexts/power-sync/PowerSync';
import { db } from '@/lib/local-api';
import { LayoutBreakpoints } from '@/constants/LayoutBreakpoints';
import { useIsWidthUp } from '~/global/hooks/useIsWidthUp';
import type {
  WorkoutSupersetRecord,
  SportActivityRecord,
  WorkoutExerciseRecord,
} from '~/powersync/generated/schema';
import { WorkoutExercise } from '~/workout/components/workout-exercise/WorkoutExercise';
import { useModal } from '@/contexts/ModalContext';
import { SportActivitySelectorModal } from '../sport-activity-selector-modal/SportActivitySelectorModal';

export interface WorkoutSupersetProps {
  /**
   * The superset data
   */
  superset: WorkoutSupersetRecord;
  /**
   * Whether the component is in loading state
   */
  isLoading?: boolean;
}

export const WorkoutSuperset = ({
  superset,
  isLoading = false,
}: WorkoutSupersetProps) => {
  const { t } = useTranslation('workout');
  const { showModal } = useModal();
  const powerSync = usePowerSync();
  const isDesktop = useIsWidthUp(LayoutBreakpoints.tablet);

  // Clean local-api mutation for deleting workout superset
  const deleteSupersetMutation = useMutation({
    mutationFn: (supersetId: string) =>
      db.Workout.mutations.deleteSuperset(powerSync, supersetId),
  });

  const { dispatchConfirmationDialog } = useDeleteProtection(() =>
    deleteSupersetMutation.mutate(superset.id),
  );

  // Clean local-api mutations for moving supersets
  const moveSupersetUpMutation = useMutation({
    mutationFn: () =>
      db.Workout.mutations.moveSupersetUp(powerSync, superset.id),
  });

  const moveSupersetDownMutation = useMutation({
    mutationFn: () =>
      db.Workout.mutations.moveSupersetDown(powerSync, superset.id),
  });

  // Fetch all supersets in the workout to determine position
  const { data: allSupersets } = useLivePowerSyncQuery(
    db.Workout.queries.findSupersetsWithExercises(superset.workoutId),
    [superset.workoutId],
  );

  const isFirstSuperset = superset.sortIndex === 0;
  const isLastSuperset =
    allSupersets && superset.sortIndex === allSupersets.length - 1;

  // Clean local-api mutation for creating workout exercise using helper
  const createWorkoutExerciseMutation = useMutation({
    mutationFn: async (params: {
      sportActivity: SportActivityRecord;
      superset: WorkoutSupersetRecord;
      sortIndex: number;
    }) => {
      const { sportActivity, superset, sortIndex } = params;

      const exercise = await db.Workout.mutations.createExerciseForSuperset(
        powerSync,
        superset.id,
        superset.workoutId,
        sportActivity,
        sortIndex,
      );

      return { exerciseId: exercise.id, sportActivity };
    },
  });

  // Fetch exercises for this superset using clean local-api
  const {
    data: exercises,
    isLoading: isLoadingExercises,
    error: exercisesError,
  } = useLivePowerSyncQuery<WorkoutExerciseRecord>(
    db.Workout.queries.findExercisesBySuperset(superset.id),
    [superset.id],
  );

  const handleAddExercise = () => {
    logger.debug('Add Exercise clicked for superset:', superset.id);

    showModal(SportActivitySelectorModal, {
      onSportActivitySelected: (sportActivity: SportActivityRecord) => {
        logger.debug(
          'Sport activity selected for superset:',
          sportActivity.name,
          'superset:',
          superset.id,
        );

        // Calculate next sort index based on existing exercises
        const nextSortIndex = exercises ? exercises.length : 0;

        // Create WorkoutExercise record using mutation
        createWorkoutExerciseMutation.mutate({
          sportActivity,
          superset,
          sortIndex: nextSortIndex,
        });
      },
    });
  };

  return (
    <View>
      {/* Superset title as divider with dropdown menu */}
      <View className="flex-row items-center justify-between mb-3">
        <Text className="font-medium text-sm text-text-secondary">
          {t('superset.title', { index: superset.sortIndex + 1 })}
        </Text>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Pressable className="p-2 -m-2 opacity-70 hover:opacity-100">
              <Icon
                as={MoreVertical}
                size={16}
                className="text-text-secondary"
              />
            </Pressable>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem
              onPress={() => moveSupersetUpMutation.mutate()}
              disabled={isFirstSuperset || moveSupersetUpMutation.isPending}
            >
              <Icon as={ChevronUp} size={16} />
              <Text>{t('superset.moveUp')}</Text>
            </DropdownMenuItem>
            <DropdownMenuItem
              onPress={() => moveSupersetDownMutation.mutate()}
              disabled={isLastSuperset || moveSupersetDownMutation.isPending}
            >
              <Icon as={ChevronDown} size={16} />
              <Text>{t('superset.moveDown')}</Text>
            </DropdownMenuItem>
            <DropdownMenuItem
              onPress={dispatchConfirmationDialog}
              disabled={deleteSupersetMutation.isPending}
              variant="destructive"
            >
              <Icon as={Trash2} size={16} className="text-destructive" />
              <Text>{t('superset.delete')}</Text>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </View>

      {/* Mobile vs desktop variants are conditionally rendered (not toggled with
          `display:none` via `md:hidden`/`hidden md:flex`): a re-rendering
          phone-`display:none` node crashes iOS New Arch (RN #52349). */}
      {!isDesktop && (
        // Mobile: single card wrapping all exercises with dividers
        <Card className="flex bg-surface rounded-xl p-4">
          {exercises?.map((exercise, index) => (
            <React.Fragment key={exercise.id}>
              {index > 0 && <View className="h-px bg-border my-1" />}
              <WorkoutExercise.Synced id={exercise.id} />
            </React.Fragment>
          ))}
        </Card>
      )}

      {isDesktop && (
        // Desktop: individual cards in horizontal scroll
        <View>
          <ScrollView
            horizontal
            showsHorizontalScrollIndicator={false}
            contentContainerClassName="gap-4"
          >
            {exercises?.map((exercise) => (
              <Card
                key={exercise.id}
                className="bg-surface rounded-xl p-4 min-w-[300px]"
              >
                <WorkoutExercise.Synced id={exercise.id} />
              </Card>
            ))}
          </ScrollView>
        </View>
      )}

      {/* Add Exercise Button — transparent, secondary text */}
      <Pressable
        onPress={handleAddExercise}
        disabled={
          isLoading ||
          isLoadingExercises ||
          createWorkoutExerciseMutation.isPending
        }
        className="flex-row items-center gap-2 self-start mt-3 mb-6 py-2"
      >
        <GlobalIcon name="Plus" size={14} className="text-text-secondary" />
        <Text className="text-sm text-text-secondary">
          {t('form.buttons.addExercise')}
        </Text>
      </Pressable>
    </View>
  );
};
